<template>
    <nord-button-group>
        <!-- Filter Field Selection Dropdown -->
        <filter-dropdown
            type="field"
            v-if="filters?.length"
            :hide-dropdown="isApplied"
            :items="filters"
            :selected-item="field"
            placeholder-text="Filter"
            @item-selected="selectField"
            label-key="fieldLabel"
            value-key="field"
            icon="interface-filter"
            :variant="field ? 'default' : 'dashed'"
        />

        <!-- Operator Selection Dropdown -->
        <filter-dropdown
            v-if="field"
            type="operator"
            :items="operators"
            :selected-item="operator"
            placeholder-text="Select"
            @item-selected="selectOperator"
        />

        <!-- Value Selection Dropdown -->
        <div v-if="shouldShowValueInput">
            <component
                v-if="field?.component && operator"
                :is="field?.component"
                :selected-item="value"
                :selected-items="multipleValues"
                :multi-select="isMultiSelect"
                @item-selected="selectValue"
                @items-selected="selectMultipleValues"
                :operator="operator"
                :field="field?.field"
            />

            <!-- Handles both single and multi-select -->
            <filter-dropdown
                v-else
                type="value"
                :items="values"
                :selected-item="value"
                :selected-items="multipleValues"
                :multi-select="isMultiSelect"
                :placeholder-text="isMultiSelect ? 'Select values' : 'Select'"
                @item-selected="selectValue"
                @items-selected="selectMultipleValues"
            />
        </div>

        <!-- Clear Filter Button -->
        <nord-button v-if="field" size="s" @click="removeFilter">
            <nord-icon name="interface-close" />
        </nord-button>
    </nord-button-group>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, watch, onMounted } from 'vue';
import FilterDropdown from 'Components/global/table/FilterDropdown.vue';

// props
const props = defineProps({
    filters: {
        type: Array,
        required: true
    },

    selectedFilter: {
        type: Object,
        default: () => ({})
    },

    isApplied: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['filter-applied', 'filter-removed']);

const field = ref(null);
const operator = ref(null);
const value = ref(null);
const multipleValues = ref([]);

const operators = computed(() => {
    return field.value?.operators || [];
});

const values = computed(() => {
    return field.value?.values || [];
});

const operatorRequiresValue = computed(() => {
    const currentOperator = operator.value;
    return !currentOperator?.doesNotRequireValue;
});

const shouldShowValueInput = computed(() => {
    return field.value && operator.value && operatorRequiresValue.value;
});

// Depends on the operator because some operators don't support multiple values
const isMultiSelect = computed(() => {
    return operator.value?.supportsMultipleValues === true;
});

watch(operators, (newOperators) => {
    if (newOperators && newOperators.length > 0 && !props.isApplied) {
        selectOperator(newOperators[0]);
    }
});

// Watch for changes in selectedFilter prop (when saved filters are applied)
watch(() => props.selectedFilter, (newFilter) => {
    if (newFilter?.field) {
        initializeFromSelectedFilter();
    }
}, { deep: true, immediate: true });

function initializeFromSelectedFilter() {
    /**
     * Early return if the selected filter is not a valid filter
     */
    const selectedFilter = props.selectedFilter;
    if (!selectedFilter?.field) return;

    /**
     * Find the filter that matches the selected filter field
     */
    const matchedFilter = props.filters.find(f => f.field === selectedFilter.field);

    /**
     * If the filter field from props.selectedFilter is not found in current props.filters
     * Notify parent to remove this filter and reset the filter
     * And return early
     */
    if (!matchedFilter) {
        emit('filter-removed', selectedFilter.field);
        resetFilter();
        return;
    }

    /**
     * Set the field and operator
     */
    field.value = matchedFilter;
    operator.value = operators.value.find(op => op.value === selectedFilter.operator);

    /**
     * If the operator supports multiple values, and the value is an array, set the multiple values
     */
    const isMultiSelect = operator.value?.supportsMultipleValues;
    const isArrayValue = Array.isArray(selectedFilter.value);

    if (isMultiSelect && isArrayValue) {
        setMultipleValues(selectedFilter, matchedFilter);
        return;
    }

    /**
     * Otherwise, set the single value
     */
    setSingleValue(selectedFilter, matchedFilter);
}

function setMultipleValues(selectedFilter, matchedFilter) {
    if (matchedFilter.component) {
        multipleValues.value = selectedFilter.value.map(val => ({value: val }));
        return
    }

    multipleValues.value = values.value.filter(v =>
        selectedFilter.value.includes(v.value)
    );
}

function setSingleValue(selectedFilter, matchedFilter) {
    if (matchedFilter.component) {
        value.value = { value: selectedFilter.value };
        return;
    }

    value.value = values.value.find(v => v.value === selectedFilter.value);
}

// Select a filter from the list
function selectField(filterField) {
    field.value = filterField;
    operator.value = null;
    value.value = null;
    multipleValues.value = [];
}

// Select an operator for the current filter
function selectOperator(op) {
    operator.value = op;
    value.value = null;
    multipleValues.value = [];
    // Auto-apply for operators that don't need values
    if (op.doesNotRequireValue) {
        applyFilter();
    }
}

// Select a single value for the current filter and the selected operator
function selectValue(val) {
    value.value = val;
    applyFilter();
}

// Select multiple values for the current filter and the selected operator
function selectMultipleValues(vals) {
    multipleValues.value = vals;
    applyFilter();
}

// Apply the complete filter (field, operator, value/values)
function applyFilter() {
    if (!field.value || !operator.value) return;

    const filterValue = getFilterValue();

    const filter = {
        field: field.value.field,
        operator: operator.value.value,
        value: filterValue,
    };

    emit('filter-applied', filter);

    // we don't want to reset the filters which are already applied
    if (!props.isApplied) {
        resetFilter();
    }
}

function getFilterValue() {
    /**
     * If the operator does not require a value, return null
     */
    if (!operatorRequiresValue.value) {
        return null;
    };

    /**
     * If the operator requires a value, and there is no value or multiple values, return null
     */
    if (!multipleValues.value?.length && !value.value) {
        return null;
    }

    /**
     * If the operator supports multiple values, return the multiple values
     */
    if (isMultiSelect.value) {
        return multipleValues.value.map(val => val.value);
    }

    /**
     * Otherwise, return the value
     */
    return value.value.value;
}

function removeFilter() {
    if (field.value) {
        emit('filter-removed', field.value.field);
    }

    resetFilter();
}

function resetFilter() {
    field.value = null;
    operator.value = null;
    value.value = null;
    multipleValues.value = [];
}
</script>
